import { isRedirect, redirect } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";

import type { AuthUser } from "~/lib/auth-middleware";
import { rethrowClerkAuthError } from "~/lib/auth/clerk-handshake";

/**
 * Resolves the Clerk session and the app user in a single round trip.
 * Returns null when not signed in. Deliberately has no auth middleware so the
 * Clerk handshake is handled here: a thrown 307 `Response` is converted to a
 * router `redirect()` (see `rethrowClerkAuthError`) so it survives seroval
 * serialization over RPC and the browser actually completes the handshake.
 */
export const getOptionalAuthUser = createServerFn({ method: "GET" }).handler(
  async (): Promise<AuthUser | null> => {
    const { auth } = await import("@clerk/tanstack-react-start/server");
    let userId: string | null;
    try {
      ({ userId } = await auth());
    } catch (error) {
      rethrowClerkAuthError(error);
    }
    if (!userId) return null;

    const { getUser } = await import("~/lib/auth-middleware");
    const user = await getUser(userId);
    const { setSentryUser } = await import("~/lib/server/sentry-context");
    setSentryUser({ id: user.id, email: user.email, type: "user" });
    return user;
  },
);

// Client-only cache of the last server-verified user. `beforeLoad` re-runs on
// every navigation, so without this every in-app page switch pays a blocking
// RPC. The cache is only trusted while Clerk's client-side state reports the
// same signed-in user — sign-out or account switch invalidates it instantly.
// Never read or written during SSR: module state on the server is shared
// across requests and would leak one visitor's auth to another.
let clientAuthUser: AuthUser | null = null;

function clerkClientUserId(): string | null {
  if (typeof window === "undefined") return null;
  // window.Clerk's global type comes from @clerk/testing, which the e2e files
  // load into the type program — no cast needed.
  return window.Clerk?.user?.id ?? null;
}

/**
 * Requires authentication for a route.
 * Checks Clerk authentication and returns the authenticated user.
 * Throws a redirect to /login if not authenticated.
 *
 * Clerk handshake redirects are converted to router redirects by
 * `getOptionalAuthUser` and propagated here; Clerk's own handshake nonce guards
 * against redirect loops, so we don't second-guess them.
 *
 * @param location - The current location object from TanStack Router
 * @returns The authenticated user
 * @throws Redirect to /login if not authenticated
 */
export async function requireAuth(location: {
  pathname: string;
  searchStr: string;
}): Promise<AuthUser> {
  let user: AuthUser | null = null;

  const liveClerkUserId = clerkClientUserId();
  if (
    clientAuthUser &&
    liveClerkUserId &&
    clientAuthUser.clerkUserId === liveClerkUserId
  ) {
    return clientAuthUser;
  }

  try {
    user = await getOptionalAuthUser();
    if (user && typeof window !== "undefined") {
      clientAuthUser = user;
    }
  } catch (error) {
    // `getOptionalAuthUser` converts a Clerk handshake (and any other Clerk
    // redirect) into a router redirect — seroval-safe over RPC. Let it
    // propagate so the browser follows it and completes the handshake.
    if (isRedirect(error)) {
      throw error;
    }

    // Any other failure means we couldn't establish the session — fall through
    // to the /login redirect below.
    console.error(
      "[requireAuth] Error fetching authenticated user:",
      error instanceof Error ? error.message : String(error),
      error instanceof Error ? error.stack : undefined,
    );
  }

  if (!user) {
    throw redirect({
      to: "/login",
      search: {
        from: "auth-failure",
        redirect: location.pathname + location.searchStr,
      } as const,
    });
  }

  return user;
}
